
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { ChatModalProps } from '@/types/chat';
import { useChat } from '@/hooks/useChat';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { useToast } from '@/hooks/use-toast';

export function ChatModal({ 
  isOpen, 
  onClose, 
  bookingId, 
  otherParticipantName,
  currentUserType
}: ChatModalProps) {
  const { messages, isLoading, isSending, sendMessage, conversation } = useChat(bookingId, currentUserType);
  const { markMessagesAsRead, refreshUnreadCounts } = useUnreadMessages({ 
    userId: undefined, // Will be set by the hook from auth context
    userType: currentUserType
  });
  const { toast } = useToast();
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [markingRetries, setMarkingRetries] = useState(0);

  // Enhanced function to mark messages as read with comprehensive retries and verification
  const markMessagesAsReadWithRetries = async () => {
    if (!isOpen || !bookingId || markingAsRead) return;
    
    try {
      setMarkingAsRead(true);
      console.log('ChatModal: Starting enhanced mark as read for booking:', bookingId);
      
      // Immediate UI feedback - optimistically update local state
      await markMessagesAsRead(bookingId);
      const success = true;
      console.log('ChatModal: Enhanced mark as read result:', success);
      
      if (!success && markingRetries < 5) { // Increased retry attempts
        // Enhanced retry logic with exponential backoff
        const retryDelay = Math.min(1000 * Math.pow(2, markingRetries), 10000);
        console.warn(`ChatModal: Retrying mark as read in ${retryDelay}ms (attempt ${markingRetries + 1}/5)`);
        
        setTimeout(async () => {
          setMarkingRetries(prev => prev + 1);
          await markMessagesAsRead(bookingId);
          const retrySuccess = true;
          
          if (retrySuccess) {
            console.log('ChatModal: Retry successful');
            // Force multiple refreshes on success
            refreshUnreadCounts();
            setTimeout(() => refreshUnreadCounts(), 1000);
            setTimeout(() => refreshUnreadCounts(), 3000);
          }
        }, retryDelay);
      } else if (!success) {
        console.error('ChatModal: Failed to mark messages as read after all attempts');
        toast({
          title: "Messages may still appear unread",
          description: "Please try refreshing the page or closing and reopening the chat.",
          variant: "destructive",
        });
      } else {
        console.log('ChatModal: Successfully marked messages as read on first attempt');
      }
      
      // Always refresh counts with multiple attempts
      refreshUnreadCounts();
      
      // Enhanced refresh schedule with more frequent updates
      [200, 800, 2000, 5000].forEach(delay => {
        setTimeout(() => {
          console.log(`ChatModal: Scheduled refresh at ${delay}ms`);
          refreshUnreadCounts();
        }, delay);
      });
      
    } catch (error) {
      console.error('ChatModal: Error in markMessagesAsReadWithRetries:', error);
      toast({
        title: "Error marking messages as read",
        description: "Please try refreshing the page.",
        variant: "destructive",
      });
    } finally {
      setMarkingAsRead(false);
    }
  };

  // Enhanced modal open effect with immediate and periodic marking
  useEffect(() => {
    if (isOpen && bookingId) {
      // Reset retry counter when opening
      setMarkingRetries(0);
      
      // Immediate mark as read
      console.log('ChatModal: Modal opened, immediately marking messages as read');
      markMessagesAsReadWithRetries();
      
      // Set up multiple intervals for different frequencies
      const immediateInterval = setInterval(() => {
        console.log('ChatModal: Periodic mark as read (immediate)');
        markMessagesAsReadWithRetries();
      }, 5000); // Every 5 seconds for first minute
      
      const regularInterval = setInterval(() => {
        console.log('ChatModal: Periodic mark as read (regular)');
        markMessagesAsReadWithRetries();
      }, 15000); // Every 15 seconds thereafter
      
      // Stop immediate interval after 1 minute
      const stopImmediateTimer = setTimeout(() => {
        clearInterval(immediateInterval);
        console.log('ChatModal: Stopped immediate interval, continuing with regular interval');
      }, 60000);
      
      return () => {
        clearInterval(immediateInterval);
        clearInterval(regularInterval);
        clearTimeout(stopImmediateTimer);
        console.log('ChatModal: Cleaned up all intervals');
      };
    }
  }, [isOpen, bookingId]);

  // Enhanced close handler with comprehensive refresh strategy
  const handleClose = () => {
    console.log('ChatModal: Closing modal and ensuring all counts are refreshed');
    
    // Immediate refresh
    refreshUnreadCounts();
    
    // Final attempt to mark any remaining messages as read
    if (bookingId) {
      console.log('ChatModal: Final mark as read attempt before closing');
      markMessagesAsRead(bookingId);
      refreshUnreadCounts();
    }
    
    // Enhanced refresh schedule with more comprehensive timing
    [100, 500, 1500, 4000, 8000].forEach(delay => {
      setTimeout(() => {
        console.log(`ChatModal: Post-close refresh at ${delay}ms`);
        refreshUnreadCounts();
      }, delay);
    });
    
    // Reset retry counter
    setMarkingRetries(0);
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md h-[500px] flex flex-col p-0">
        <DialogHeader className="p-4 border-b">
          <DialogTitle>Chat with {otherParticipantName}</DialogTitle>
          <DialogDescription>
            Appointment conversation
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          </div>
        ) : (
          <>
            <MessageList 
              messages={messages} 
              otherParticipantName={otherParticipantName}
            />
            <MessageInput 
              onSendMessage={sendMessage} 
              isSending={isSending}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
